import classNames from 'classnames';
import { Anchor } from 'mapbox-gl';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import logError from '@alltrails/core/utils/logError';
import { DropdownMenuItems } from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Add from '@alltrails/denali/icons/Add';
import Check from '@alltrails/denali/icons/Check';
import Copy from '@alltrails/denali/icons/Copy';
import DrawPath from '@alltrails/denali/icons/DrawPath';
import PaddingAwarePopup from '../PaddingAwarePopup/PaddingAwarePopup';
import styles from './ClickableIcons.module.scss';
import { popupOffset } from './constants';
import type { PopupFeature } from './usePoiInteractions';

type ClickableIconsPopupProps = {
  popupFeature: PopupFeature;
  isEditing?: boolean;
  onAddToRoute: () => void;
};

const ClickableIconsPopup = ({ popupFeature, isEditing, onAddToRoute }: ClickableIconsPopupProps) => {
  const intl = useIntl();
  const [isCopied, setIsCopied] = useState(false);

  const onClickCopyCoordinates = useCallback(() => {
    if (!popupFeature.geometry) {
      return;
    }
    navigator.clipboard
      .writeText(`${popupFeature.geometry[1].toFixed(4)}, ${popupFeature.geometry[0].toFixed(4)}`)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1500);
      })
      .catch(err => {
        logError(new Error('Failed to copy coordinates to clipboard', { cause: err }));
      });
  }, [popupFeature.geometry]);

  if (!popupFeature.geometry) {
    return null;
  }

  return (
    <PaddingAwarePopup
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      offset={popupOffset as Partial<Record<Anchor, [number, number]>>}
      latitude={popupFeature.geometry[1]}
      longitude={popupFeature.geometry[0]}
      maxWidth="327"
    >
      <div className={classNames(styles.container, { [styles.hasName]: !!popupFeature.name })}>
        {popupFeature.name && (
          <>
            <div className={classNames(styles.topContent, { [styles.hasName]: !!popupFeature.name })}>
              <Typography variant="text200bold">{popupFeature.name}</Typography>
              <div className={styles.coordinatesContainer}>
                <Typography variant="text100" color="secondary">
                  {popupFeature.geometry[1].toFixed(4)}, {popupFeature.geometry[0].toFixed(4)}
                </Typography>
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={onClickCopyCoordinates}
                  title={intl.formatMessage({ defaultMessage: 'Copy coordinates' })}
                  icon={{ Component: isCopied ? Check : Copy, color: '--color-text-secondary' }}
                />
              </div>
            </div>
            <div className={styles.divider} />
          </>
        )}
        <DropdownMenuItems
          options={[
            {
              title: (
                <div className={styles.buildCustomRouteButton}>
                  {isEditing ? <Add size="xs" /> : <DrawPath size="xs" />}
                  <Typography variant="text150bold">
                    {isEditing ? <FormattedMessage defaultMessage="Add to route" /> : <FormattedMessage defaultMessage="Build custom route" />}
                  </Typography>
                </div>
              ),
              onClick: onAddToRoute
            }
          ]}
        />
      </div>
    </PaddingAwarePopup>
  );
};

export default ClickableIconsPopup;
